import { useCallback } from 'react';

import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import { TreatmentRequest } from '@/models/diagnosis';
import { EDoctorFilterType } from '@/models/diagnosis/doctor-filter';
import { NonF2fDoctor } from '@/models/doctor/telemedicine';

import ClickLogger from '@/components/common/ClickLogger';
import { Button } from 'doctornow-design-system';
import React from 'react';
import TelemedicineDoctorItem from './TelemedicineDoctorItem';
import TelemedicineDoctorListEmpty from './TelemedicineDoctorListEmpty';

const DOCTOR_LIST_LOAD_MORE_COUNT = 10;

interface Props {
  searchDoctors?: NonF2fDoctor[];
  hasNextPageSearchDoctors: boolean;
  fetchNextPageSearchDoctors: () => void;
  onOpenDoctorDetailBottomSheet: (doctorId: string) => void;
  getDoctorDetailOpenCount: () => number;
  treatmentRequest: TreatmentRequest;
  selectedFilters?: EDoctorFilterType[];
}

const TelemedicineDoctorList = ({
  searchDoctors,
  hasNextPageSearchDoctors,
  fetchNextPageSearchDoctors,
  onOpenDoctorDetailBottomSheet,
  getDoctorDetailOpenCount,
  treatmentRequest,
  selectedFilters,
}: Props) => {
  const getScrollDepth = useCallback(() => {
    const currentScrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;

    const footerElement = document.querySelector('footer');
    const footerHeight = footerElement ? footerElement.offsetHeight : 0;

    const maxScrollableHeight = documentHeight - windowHeight - footerHeight;

    let scrollDepth = 0;
    if (maxScrollableHeight > 0) {
      scrollDepth = Math.min(100, Math.max(0, Math.round((currentScrollY / maxScrollableHeight) * 100)));
    }

    return scrollDepth;
  }, []);

  const handleDoctorClick = (doctorId: string) => (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onOpenDoctorDetailBottomSheet(doctorId);
  };

  const showEmptyView = !searchDoctors || searchDoctors.length === 0;

  if (showEmptyView) {
    return <TelemedicineDoctorListEmpty />;
  }

  return (
    <>
      <div>
        {searchDoctors?.map((doctor, index, array) => (
          <React.Fragment key={doctor.doctorPublicId}>
            <ClickLogger
              params={{
                eventName: EWebTreatmentRequestLog.ClickReqDoctorSelect,
                payload: () => {
                  const scrollDepth = getScrollDepth();
                  const totalDoctorsCount = searchDoctors?.length ?? 0;
                  const hasLessThan10Doctors = totalDoctorsCount < 10;
                  const doctorDetailOpenCount = getDoctorDetailOpenCount();

                  return {
                    doctorId: doctor.doctorPublicId,
                    scrollDepth,
                    totalDoctorsCount,
                    hasLessThan10Doctors: hasLessThan10Doctors.toString(),
                    navigationCount: doctorDetailOpenCount,
                    categoryId: treatmentRequest.form.categoryId ?? '',
                    diagnosisType: treatmentRequest.form.categoryType ?? '',
                  };
                },
              }}
            >
              <div onClick={handleDoctorClick(doctor.doctorPublicId)}>
                <TelemedicineDoctorItem doctor={doctor} selectedFilters={selectedFilters} />
              </div>
            </ClickLogger>
            {index !== array.length - 1 && <div className='h-[1px] w-full bg-gray-30' />}
          </React.Fragment>
        ))}
      </div>
      {hasNextPageSearchDoctors && (
        <div className='flex flex-col gap-[10px] px-[24px] pt-[16px] pb-[40px]'>
          <ClickLogger
            params={{
              eventName: EWebTreatmentRequestLog.ClickLoadMoreDoctorsButton,
            }}
          >
            <Button size='l' buttonColor='gray' buttonStyle='outlined' onClick={fetchNextPageSearchDoctors}>
              <span className='typography-ui-17sb text-gray-600'>의사 {DOCTOR_LIST_LOAD_MORE_COUNT}명 더 보기</span>
            </Button>
          </ClickLogger>
        </div>
      )}
    </>
  );
};

export default TelemedicineDoctorList;
